import { useEffect, useState } from "react";
import api from "./api";

/**
 * Issue #142: `PerformanceTracking.jsx`'s "required fields" check used to
 * be a hand-typed list (`opponent`, `match_date`) that silently drifted
 * from what the API's `matchCreateSchema` actually required (`competition`
 * and `ground` too) — a missing competition/ground passed the client
 * check, 400'd at the API, and the user got a generic "Failed to save
 * match" naming nothing.
 *
 * Rather than hand-copy the field list a second time (which is exactly
 * how the drift happened), the client fetches it from the API — see
 * `GET /matches/required-fields` / `GET /training-sessions/required-fields`
 * (`api/src/routes/matches.ts` / `trainingSessions.ts`), both computed by
 * introspecting the actual create schema (`api/src/lib/requiredFields.ts`),
 * not hand-typed there either. The client's required-set IS the server's
 * required-set, fetched over the wire — the two cannot silently diverge,
 * because there is only one list, not two kept in sync by hand.
 *
 * A successful answer is cached per `path` for the tab's lifetime — this is
 * static schema metadata, not player data, so there's nothing to invalidate.
 * A fetch failure resolves to `null` (not `[]`) so callers can tell "the API
 * said there are zero required fields" apart from "we don't know" and fall
 * back to letting the server's own 400 catch it, rather than silently
 * skipping client-side validation entirely. A failure is NOT cached: the
 * next mount asks again (issue #783).
 *
 * Issue #783: `null` from `fetchRequiredFields` means only "failed". "In
 * flight" is its own state, so a form's Save button can be disabled while
 * the fetch is loading and stay usable once it has failed (the server's own
 * 400 answers instead of a dead button). The three states are named below and
 * `useRequiredFields` returns them.
 */
const cache = new Map();

export function fetchRequiredFields(path) {
  if (!cache.has(path)) {
    cache.set(
      path,
      api
        .get(path)
        .then((r) => r.data.required)
        .catch(() => {
          cache.delete(path);
          return null;
        })
    );
  }
  return cache.get(path);
}

export const REQUIRED_LOADING = "loading"; // fetch in flight
export const REQUIRED_KNOWN = "known"; // the API answered; fields is the list
export const REQUIRED_UNKNOWN = "unknown"; // the fetch failed; the server's 400 decides

export function requiredStatus(resolved, fields) {
  if (!resolved) return REQUIRED_LOADING;
  return Array.isArray(fields) ? REQUIRED_KNOWN : REQUIRED_UNKNOWN;
}

/** Save may be pressed in every state but "in flight". */
export function canSubmitRequired(status) {
  return status !== REQUIRED_LOADING;
}

/** The list to validate against client-side; none when it is unknown. */
export function fieldsToCheck(fields) {
  return Array.isArray(fields) ? fields : [];
}

export function useRequiredFields(path) {
  const [state, setState] = useState({ resolved: false, fields: null });
  useEffect(() => {
    let live = true;
    fetchRequiredFields(path).then((fields) => {
      if (live) setState({ resolved: true, fields });
    });
    return () => {
      live = false;
    };
  }, [path]);
  return { status: requiredStatus(state.resolved, state.fields), fields: fieldsToCheck(state.fields) };
}
